import { useCallback, useEffect, useMemo, useState } from 'react';
import { settingsApi, platformApi, syncApi } from '../../api';
import { googleOAuthApi } from '../../api/googleOAuthApi';
import { useBranch } from '../../context/BranchContext';
import { toast } from 'sonner';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
  EyeOff,
  Globe,
  Info,
  Key,
  Loader2,
  RefreshCw,
  RotateCw,
  Save,
  Settings,
  ShieldCheck,
  Wifi,
  WifiOff,
  XCircle,
  Zap,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import PlatformIcon, { getPlatformLabel, PLATFORM_CONFIG } from '../../components/PlatformIcon';

const PLATFORM_DESCRIPTIONS = {
  google: 'Authorize CredBloom to read and reply to reviews for your Google Business Profile',
  yelp: 'Pull your Yelp Fusion API rating, review snippets and photos',
  trustpilot: 'Read and reply to verified Trustpilot reviews',
  foursquare: 'Read tips, ratings and check-ins for your venues',
  tripadvisor: 'Manage TripAdvisor hotel and restaurant reviews',
  facebook: 'Monitor Facebook Page recommendations and comments',
  instagram: 'View and engage with Instagram posts and comments',
  linkedin: 'Manage LinkedIn company Page posts and comments',
  x: 'Monitor X mentions and replies',
  youtube: 'Manage YouTube video comments and replies',
  reddit: 'Monitor and reply to Reddit posts and comments',
  booking: 'Manage Booking.com property reviews',
  expedia: 'Manage Expedia, Hotels.com and Vrbo reviews',
  hotels_com: 'Hotels.com reviews through Expedia Partner Central',
  agoda: 'Manage Agoda guest reviews through YCS Smart Connect',
  opentable: 'Manage OpenTable restaurant reviews',
  viator: 'Manage Viator tour and activity reviews',
  getyourguide: 'Manage GetYourGuide activity reviews',
  airbnb: 'Manage Airbnb host reviews for approved partners',
  zillow: 'Read Zillow Premier Agent ratings and leads',
  realtor: 'Read Realtor.com agent ratings and reviews',
};

const CATEGORY_ORDER = ['Reviews & Local', 'Social', 'Hospitality', 'Travel', 'Properties', 'Other'];
const CATEGORY_LABELS = {
  'Reviews & Local': ['Reviews & Local', 'General review and local-listing platforms'],
  Social: ['Social', 'Social media engagement and community management'],
  Hospitality: ['Hospitality', 'Hotels, restaurants and OTAs'],
  Travel: ['Travel & Tours', 'Tours, activities and short-stay platforms'],
  Properties: ['Properties & Real Estate', 'Real-estate listings and agent ratings'],
  Other: ['Other', 'Additional integrations'],
};
const SYNCABLE_PLATFORMS = ['google', 'facebook', 'youtube', 'reddit', 'yelp', 'trustpilot', 'foursquare', 'zomato', 'justdial'];

function LinkifiedText({ text }) {
  const parts = String(text || '').split(/(https?:\/\/[^\s<>")]+)/g);
  return parts.map((part, index) => (
    part.startsWith('http') ? (
      <a key={`${part}-${index}`} href={part} target="_blank" rel="noreferrer" className="break-all text-primary underline underline-offset-2">{part}</a>
    ) : <span key={`${part}-${index}`}>{part}</span>
  ));
}

function StatusIcon({ status }) {
  if (status === 'connected') return <CheckCircle size={16} className="text-emerald-500" />;
  if (status === 'error' || status === 'selection_required') return <AlertCircle size={16} className="text-red-500" />;
  return <XCircle size={16} className="text-muted-foreground" />;
}

function errorMessage(error, fallback) {
  return error?.response?.data?.detail || fallback;
}

export default function Platforms() {
  const { currentBranch } = useBranch();
  const [platforms, setPlatforms] = useState([]);
  const [platformConfigs, setPlatformConfigs] = useState({});
  const [credentials, setCredentials] = useState({});
  const [googleStatus, setGoogleStatus] = useState({ admin_configured: false, connection: null });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [testing, setTesting] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [showTrustpilotReply, setShowTrustpilotReply] = useState(false);
  const [credentialForm, setCredentialForm] = useState({
    client_id: '',
    client_secret: '',
    api_key: '',
    trustpilot_username: '',
    trustpilot_password: '',
  });

  const branchParams = useMemo(
    () => (currentBranch ? { branch_id: currentBranch.id } : {}),
    [currentBranch],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [platformsResponse, configsResponse, credentialsResponse, googleResponse] = await Promise.all([
        settingsApi.getPlatforms(branchParams),
        platformApi.getConfigs(),
        platformApi.getCredentials(branchParams).catch(() => ({ data: [] })),
        googleOAuthApi.getStatus(branchParams).catch(() => ({ data: { admin_configured: false, connection: null } })),
      ]);
      setPlatforms(platformsResponse.data || []);
      setPlatformConfigs(configsResponse.data || {});
      const credentialMap = {};
      (credentialsResponse.data || []).forEach((item) => { credentialMap[item.platform] = item; });
      setCredentials(credentialMap);
      setGoogleStatus(googleResponse.data || { admin_configured: false, connection: null });
    } catch (error) {
      console.error(error);
      toast.error('Failed to load platform connections');
    } finally {
      setLoading(false);
    }
  }, [branchParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCredentialsDialog = (platform) => {
    if (platform === 'google') return;
    const existing = credentials[platform] || {};
    const additional = existing.additional_config || {};
    setSelectedPlatform(platform);
    setCredentialForm({
      client_id: existing.oauth_client_id || '',
      client_secret: '',
      api_key: '',
      trustpilot_username: '',
      trustpilot_password: '',
    });
    setShowTrustpilotReply(Boolean(platform === 'trustpilot' && (additional.has_trustpilot_username || additional.has_trustpilot_password)));
    setInstructionsOpen(!credentials[platform]);
    setShowSecrets({});
    setDialogOpen(true);
  };

  const saveCredentials = async () => {
    const config = platformConfigs[selectedPlatform];
    if (!config) return;
    if (config.required_fields?.includes('client_id') && !credentialForm.client_id.trim()) return toast.error('Client ID / App ID is required');
    if (config.required_fields?.includes('client_secret') && !credentialForm.client_secret.trim() && !credentials[selectedPlatform]?.has_client_secret) return toast.error('Client Secret is required');
    if (config.required_fields?.includes('api_key') && !credentialForm.api_key.trim() && !credentials[selectedPlatform]?.has_api_key) return toast.error('API Key is required');

    setSavingCredentials(true);
    try {
      const additionalConfig = {};
      if (selectedPlatform === 'trustpilot') {
        if (credentialForm.trustpilot_username.trim()) additionalConfig.trustpilot_username = credentialForm.trustpilot_username.trim();
        if (credentialForm.trustpilot_password) additionalConfig.trustpilot_password = credentialForm.trustpilot_password;
      }
      await platformApi.saveCredentials({
        platform: selectedPlatform,
        client_id: credentialForm.client_id.trim() || null,
        client_secret: credentialForm.client_secret.trim() || null,
        api_key: credentialForm.api_key.trim() || null,
        additional_config: Object.keys(additionalConfig).length ? additionalConfig : null,
      }, branchParams);
      toast.success(`${getPlatformLabel(selectedPlatform)} credentials saved`);
      setDialogOpen(false);
      await fetchData();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to save credentials'));
    } finally {
      setSavingCredentials(false);
    }
  };

  const connect = async (platform) => {
    setConnecting(platform);
    try {
      if (platform === 'google') {
        if (!googleStatus.admin_configured) {
          throw new Error('Google Business connection is not available yet. Ask the CredBloom Super Admin to finish Google OAuth setup.');
        }
        const { data } = await googleOAuthApi.getAuthorizeUrl(branchParams);
        localStorage.setItem('oauth_state', data.state);
        localStorage.setItem('oauth_platform', 'google');
        localStorage.setItem('oauth_branch_id', currentBranch?.id || '');
        window.location.href = data.auth_url;
        return;
      }

      const config = platformConfigs[platform];
      if (!credentials[platform]) {
        openCredentialsDialog(platform);
        throw new Error('Configure the platform credentials first.');
      }
      if (config?.auth_type === 'api_key') {
        await settingsApi.connectPlatform(platform, branchParams);
        toast.success(`${getPlatformLabel(platform)} connected`);
        await fetchData();
        return;
      }
      const params = { ...branchParams, redirect_uri: `${window.location.origin}/admin/oauth/callback` };
      const { data } = await platformApi.getOAuthUrl(platform, params);
      localStorage.setItem('oauth_state', data.state);
      localStorage.setItem('oauth_platform', platform);
      localStorage.setItem('oauth_branch_id', currentBranch?.id || '');
      window.location.href = data.auth_url;
    } catch (error) {
      const message = error?.response ? errorMessage(error, 'Failed to start connection') : error.message;
      toast.error(message);
      setConnecting(null);
    }
  };

  const disconnect = async (platform) => {
    if (!window.confirm(`Disconnect ${getPlatformLabel(platform)}?`)) return;
    try {
      if (platform === 'google') await googleOAuthApi.disconnect(branchParams);
      else await settingsApi.disconnectPlatform(platform, branchParams);
      toast.success(`${getPlatformLabel(platform)} disconnected`);
      await fetchData();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to disconnect'));
    }
  };

  const testConnection = async (platform) => {
    if (!SYNCABLE_PLATFORMS.includes(platform)) return toast.info('Connection testing is not available for this platform');
    setTesting(platform);
    try {
      const { data } = await syncApi.testConnection(platform, branchParams);
      setTestResults((current) => ({ ...current, [platform]: data }));
      if (data.status === 'connected') toast.success(`${getPlatformLabel(platform)} API connection verified`);
      else toast.error(data.error || data.message || 'Connection test failed');
    } catch (error) {
      toast.error(errorMessage(error, 'Connection test failed'));
    } finally {
      setTesting(null);
    }
  };

  const syncPlatform = async (platform) => {
    if (!SYNCABLE_PLATFORMS.includes(platform)) return toast.info('Sync is not available for this platform');
    setSyncing(platform);
    try {
      const { data } = await syncApi.syncPlatform(platform, branchParams);
      if (data.status === 'error') toast.error(data.error || 'Sync failed');
      else toast.success(data.status === 'in_progress' ? `Sync started for ${getPlatformLabel(platform)}` : `${getPlatformLabel(platform)} sync completed`);
      await fetchData();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to start sync'));
    } finally {
      setSyncing(null);
    }
  };

  if (loading) {
    return <div className="space-y-3">{[1, 2, 3].map((item) => <div key={item} className="h-28 animate-pulse rounded-xl bg-muted" />)}</div>;
  }

  const googleEnabled = platforms.some((item) => item.platform === 'google');
  const configuredCount = Object.keys(credentials).filter((key) => key !== 'google').length + (googleEnabled && googleStatus.admin_configured ? 1 : 0);
  const connectedCount = platforms.filter((item) => item.status === 'connected').length;
  const grouped = {};
  platforms.forEach((item) => {
    const category = platformConfigs[item.platform]?.category || 'Other';
    if (!grouped[category]) grouped[category] = [];
    grouped[category].push(item);
  });
  const categories = [
    ...CATEGORY_ORDER.filter((category) => grouped[category]?.length),
    ...Object.keys(grouped).filter((category) => !CATEGORY_ORDER.includes(category)),
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Platform Connections</h1>
          <p className="text-sm text-muted-foreground">{connectedCount} connected, {configuredCount} configured</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="gap-2"><RefreshCw size={14} /> Refresh</Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          ['Connected', connectedCount, 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20'],
          ['Configured', configuredCount, 'bg-blue-50 text-blue-600 dark:bg-blue-900/20'],
          ['Not Connected', platforms.length - connectedCount, 'bg-muted text-muted-foreground'],
        ].map(([label, count, style]) => (
          <div key={label} className={`rounded-xl p-4 text-center ${style}`}>
            <p className="text-2xl font-bold">{count}</p><p className="text-xs">{label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
        <div className="flex items-start gap-3">
          <Info size={18} className="mt-0.5 shrink-0 text-blue-600" />
          <div className="text-xs leading-relaxed text-blue-700 dark:text-blue-300">
            <p className="mb-1 text-sm font-medium">Simplified Google connection</p>
            Google developer credentials are managed centrally by CredBloom. Click <strong>Connect Google</strong>, approve access using the Google account that manages your Business Profile, and select the correct business. Other platforms may still require their own developer or partner credentials.
          </div>
        </div>
      </div>

      {categories.map((category) => {
        const [title, subtitle] = CATEGORY_LABELS[category] || [category, ''];
        const items = grouped[category];
        return (
          <section key={category} className="space-y-3">
            <div className="border-b pb-2">
              <h2 className="text-lg font-semibold">{title} <span className="ml-2 text-xs font-normal text-muted-foreground">{items.filter((item) => item.status === 'connected').length}/{items.length} connected</span></h2>
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {items.map((platform) => {
                const key = platform.platform;
                const isGoogle = key === 'google';
                const isConnected = platform.status === 'connected';
                const isProblem = platform.status === 'error' || platform.status === 'selection_required';
                const config = platformConfigs[key] || {};
                const visual = PLATFORM_CONFIG[key];
                const hasCredentials = isGoogle ? googleStatus.admin_configured : Boolean(credentials[key]);
                const selectedBusiness = platform.google_business_name || googleStatus.connection?.google_business_name;
                const selectedAddress = platform.google_business_address || googleStatus.connection?.google_business_address;
                const result = testResults[key];

                return (
                  <Card key={key} className={`${isConnected ? 'border-emerald-200 dark:border-emerald-900' : isProblem ? 'border-red-200 dark:border-red-900' : ''}`}>
                    <CardContent className="pt-5">
                      <div className="flex items-start gap-4">
                        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${visual?.bg || 'bg-muted'}`}><PlatformIcon platform={key} size={24} /></div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold">{getPlatformLabel(key)}</p><StatusIcon status={platform.status} />
                            {config.partner_only && <Badge variant="outline" className="text-[10px] text-amber-700">Partner-only</Badge>}
                          </div>
                          <p className="mb-3 mt-1 text-xs text-muted-foreground">{PLATFORM_DESCRIPTIONS[key] || 'Platform integration'}</p>

                          <div className="mb-3 flex flex-wrap gap-2">
                            {isGoogle ? (
                              <Badge variant="outline" className={hasCredentials ? 'border-blue-200 text-blue-700' : 'border-amber-200 text-amber-700'}>
                                <ShieldCheck size={11} className="mr-1" /> {hasCredentials ? 'Managed by CredBloom' : 'Super Admin setup required'}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className={hasCredentials ? 'border-blue-200 text-blue-700' : 'border-amber-200 text-amber-700'}>
                                <Key size={11} className="mr-1" /> {hasCredentials ? 'Credentials configured' : 'Credentials needed'}
                              </Badge>
                            )}
                            <Badge variant="outline" className={isConnected ? 'border-emerald-200 text-emerald-700' : isProblem ? 'border-red-200 text-red-700' : 'text-muted-foreground'}>
                              {isConnected ? 'Connected' : platform.status === 'selection_required' ? 'Business selection required' : isProblem ? 'Connection issue' : 'Not connected'}
                            </Badge>
                          </div>

                          {isGoogle && selectedBusiness && (
                            <div className="mb-3 rounded-lg bg-emerald-50 p-2.5 text-xs dark:bg-emerald-950/20">
                              <p className="font-medium text-emerald-700 dark:text-emerald-300">{selectedBusiness}</p>
                              {selectedAddress && <p className="mt-0.5 text-emerald-700/80 dark:text-emerald-300/80">{selectedAddress}</p>}
                            </div>
                          )}

                          {platform.error && <p className="mb-2 text-xs text-red-600">{platform.error}</p>}
                          {isConnected && platform.last_synced_at && <p className="mb-2 text-xs text-emerald-600">Last synced: {new Date(platform.last_synced_at).toLocaleString()}</p>}

                          <div className="flex flex-wrap gap-2">
                            {!isGoogle && (
                              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => openCredentialsDialog(key)}><Settings size={11} /> Configure</Button>
                            )}
                            {!isConnected ? (
                              <Button size="sm" className="h-7 gap-1 text-xs" disabled={connecting === key || !hasCredentials} onClick={() => connect(key)}>
                                {connecting === key && <Loader2 size={11} className="animate-spin" />}
                                {isGoogle ? 'Connect Google' : config.auth_type === 'oauth2' ? 'Authorize' : 'Connect'}
                              </Button>
                            ) : (
                              <>
                                {SYNCABLE_PLATFORMS.includes(key) && (
                                  <>
                                    <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={testing === key} onClick={() => testConnection(key)}>{testing === key ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />} Test</Button>
                                    <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={syncing === key} onClick={() => syncPlatform(key)}>{syncing === key ? <Loader2 size={11} className="animate-spin" /> : <RotateCw size={11} />} Sync</Button>
                                  </>
                                )}
                                <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => disconnect(key)}>Disconnect</Button>
                              </>
                            )}
                          </div>

                          {result && isConnected && (
                            <div className={`mt-3 rounded-lg border p-2.5 text-xs ${result.status === 'connected' ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20' : result.status === 'rate_limited' ? 'border-amber-200 bg-amber-50 dark:bg-amber-950/20' : 'border-red-200 bg-red-50 dark:bg-red-950/20'}`}>
                              <div className="flex items-center gap-2 font-medium">
                                {result.status === 'connected' ? <Wifi size={12} className="text-emerald-600" /> : <WifiOff size={12} className="text-red-600" />}
                                {result.status === 'connected' ? 'API Connected' : result.error || result.message || 'Connection issue'}
                              </div>
                              {result.business_name && <p className="mt-1 text-muted-foreground">Business: {result.business_name}</p>}
                              {result.details_url && <a href={result.details_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-primary underline"><ExternalLink size={11} /> {result.details_label || 'Open details'}</a>}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        );
      })}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100vw-1rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
          <DialogHeader className="shrink-0 border-b px-5 pb-4 pt-5 sm:px-6">
            <DialogTitle className="flex items-center gap-2 pr-6 text-base">
              {selectedPlatform && <PlatformIcon platform={selectedPlatform} size={20} />}
              Configure {selectedPlatform && getPlatformLabel(selectedPlatform)}
            </DialogTitle>
            <DialogDescription>Enter the developer or partner credentials required by this platform.</DialogDescription>
          </DialogHeader>

          {selectedPlatform && platformConfigs[selectedPlatform] && (
            <>
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 sm:px-6">
                {platformConfigs[selectedPlatform].notes && <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs leading-relaxed text-blue-700 dark:border-blue-900 dark:bg-blue-950/20 dark:text-blue-300">{platformConfigs[selectedPlatform].notes}</div>}
                <div className="flex flex-wrap gap-2">
                  {platformConfigs[selectedPlatform].apply_url && <a href={platformConfigs[selectedPlatform].apply_url} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="gap-1.5 text-xs"><ExternalLink size={11} /> Developer Console</Button></a>}
                  {platformConfigs[selectedPlatform].docs_url && <a href={platformConfigs[selectedPlatform].docs_url} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="gap-1.5 text-xs"><Info size={11} /> API Docs</Button></a>}
                </div>

                <Collapsible open={instructionsOpen} onOpenChange={setInstructionsOpen}>
                  <CollapsibleTrigger asChild><Button variant="outline" size="sm" className="w-full justify-between text-xs"><span className="flex items-center gap-2"><Info size={13} /> Setup Instructions</span>{instructionsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</Button></CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 rounded-lg border bg-muted/30 p-4">
                    <ol className="space-y-2.5 text-xs">
                      {(platformConfigs[selectedPlatform].instructions || []).map((step, index) => (
                        <li key={`${selectedPlatform}-${index}`} className="flex gap-3"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">{index + 1}</span><span className="leading-relaxed"><LinkifiedText text={step} /></span></li>
                      ))}
                    </ol>
                  </CollapsibleContent>
                </Collapsible>

                <div className="space-y-3">
                  {platformConfigs[selectedPlatform].required_fields?.includes('client_id') && (
                    <div className="space-y-1.5"><Label>Client ID / App ID *</Label><Input value={credentialForm.client_id} onChange={(event) => setCredentialForm((value) => ({ ...value, client_id: event.target.value }))} placeholder="Enter Client ID or business identifier" /></div>
                  )}
                  {platformConfigs[selectedPlatform].required_fields?.includes('client_secret') && (
                    <div className="space-y-1.5"><Label>Client Secret / App Secret *</Label><div className="relative"><Input type={showSecrets.client_secret ? 'text' : 'password'} value={credentialForm.client_secret} onChange={(event) => setCredentialForm((value) => ({ ...value, client_secret: event.target.value }))} placeholder={credentials[selectedPlatform]?.has_client_secret ? 'Leave blank to keep existing secret' : 'Enter Client Secret'} className="pr-10" /><button type="button" onClick={() => setShowSecrets((value) => ({ ...value, client_secret: !value.client_secret }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{showSecrets.client_secret ? <EyeOff size={15} /> : <Eye size={15} />}</button></div></div>
                  )}
                  {platformConfigs[selectedPlatform].required_fields?.includes('api_key') && (
                    <div className="space-y-1.5"><Label>API Key *</Label><div className="relative"><Input type={showSecrets.api_key ? 'text' : 'password'} value={credentialForm.api_key} onChange={(event) => setCredentialForm((value) => ({ ...value, api_key: event.target.value }))} placeholder={credentials[selectedPlatform]?.has_api_key ? 'Leave blank to keep existing API key' : 'Enter API Key'} className="pr-10" /><button type="button" onClick={() => setShowSecrets((value) => ({ ...value, api_key: !value.api_key }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{showSecrets.api_key ? <EyeOff size={15} /> : <Eye size={15} />}</button></div></div>
                  )}

                  {selectedPlatform === 'trustpilot' && (
                    <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-3 dark:border-purple-900 dark:bg-purple-950/20">
                      <button type="button" onClick={() => setShowTrustpilotReply((value) => !value)} className="flex w-full items-center justify-between text-left"><span><span className="text-sm font-medium">Optional automatic reply login</span><span className="mt-0.5 block text-xs text-muted-foreground">Stored encrypted and used only when posting Trustpilot replies.</span></span>{showTrustpilotReply ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</button>
                      {showTrustpilotReply && <div className="mt-3 space-y-3 border-t border-purple-200 pt-3 dark:border-purple-900"><Input type="email" value={credentialForm.trustpilot_username} onChange={(event) => setCredentialForm((value) => ({ ...value, trustpilot_username: event.target.value }))} placeholder="Trustpilot Business account email" /><Input type="password" value={credentialForm.trustpilot_password} onChange={(event) => setCredentialForm((value) => ({ ...value, trustpilot_password: event.target.value }))} placeholder="Trustpilot Business account password" autoComplete="new-password" /></div>}
                    </div>
                  )}
                </div>

                {platformConfigs[selectedPlatform].auth_type === 'oauth2' && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs dark:border-amber-900 dark:bg-amber-950/20"><p className="font-medium text-amber-700 dark:text-amber-300">Authorized Redirect URI</p><p className="mt-1 break-all rounded bg-white/50 px-2 py-1 font-mono text-amber-700 dark:bg-black/20 dark:text-amber-200">{window.location.origin}/admin/oauth/callback</p></div>}
              </div>
              <div className="flex shrink-0 gap-2 border-t bg-background px-5 py-4 sm:px-6"><Button variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>Cancel</Button><Button className="flex-1 gap-2" disabled={savingCredentials} onClick={saveCredentials}>{savingCredentials ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Credentials</Button></div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
